import { useState, useMemo } from 'react';
import { CaretDown, Plus, Link as LinkIcon, MagnifyingGlass, SealQuestion, WifiHigh, Devices, DeviceMobile, IdentificationBadge, Laptop } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';

// MAC-only な name か？ (例: "C8-F0-9E-36-ED-D0" / "aa:bb:cc:dd:ee:ff")
const isMacLike = (s) => !!s && /^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i.test(String(s).trim());

// 名前から端末カテゴリを推定 — グループ表示 + アイコン用
const inferKind = (name) => {
  const n = String(name || '').toLowerCase();
  if (!n) return 'unknown';
  if (/iphone|ipad|android|galaxy|pixel|xperia|redmi|huawei|mi-\d/i.test(n)) return 'mobile';
  if (/mac(book)?|surface|thinkpad|latitude|inspiron|vaio|dynabook|zenbook|lenovo|dell|hp[- ]/i.test(n)) return 'laptop';
  if (/printer|epson|canon|brother|kyocera|複合機/i.test(n)) return 'printer';
  if (/tv|chromecast|firetv|shield|switch|playstation|xbox/i.test(n)) return 'media';
  return 'named';
};

const KIND_META = {
  mobile:  { label: 'モバイル',   Icon: DeviceMobile,       cls: 'text-purple-700 bg-purple-100 border-purple-200' },
  laptop:  { label: 'ノートPC',   Icon: Laptop,             cls: 'text-sky-700 bg-sky-100 border-sky-200' },
  printer: { label: 'プリンター', Icon: IdentificationBadge, cls: 'text-orange-700 bg-orange-100 border-orange-200' },
  media:   { label: 'メディア',   Icon: Devices,            cls: 'text-fuchsia-700 bg-fuchsia-100 border-fuchsia-200' },
  named:   { label: '命名済み',   Icon: Devices,            cls: 'text-emerald-700 bg-emerald-100 border-emerald-200' },
  unknown: { label: '無名',       Icon: SealQuestion,       cls: 'text-zinc-500 bg-zinc-100 border-zinc-200' },
};

const DEVICE_TYPE_LABEL = { gateway: 'ゲートウェイ', switch: 'スイッチ', ap: 'AP' };

const RowActions = ({ row, testidType, onLink, onRegister }) => (
  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
    <button type="button" onClick={() => onLink(row)}
      className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 hover:text-emerald-900 px-1.5 py-0.5 rounded hover:bg-emerald-50"
      title="既存の資産に紐付け" data-testid={`it-link-${testidType}-${row.mac}`}>
      <LinkIcon size={9} weight="bold" /> 紐付け
    </button>
    <button type="button" onClick={() => onRegister(row)}
      className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-sky-700 hover:text-sky-900 px-1.5 py-0.5 rounded hover:bg-sky-100"
      title="新規資産として登録" data-testid={`it-register-${testidType}-${row.mac}`}>
      <Plus size={9} weight="bold" /> 登録
    </button>
  </div>
);

const GROUP_COLLAPSED_LS = 'it_omada_unmatched_collapsed_v1';
const DEFAULT_OPEN = new Set(['infra']); // インフラ機器 は既定で展開、他は畳んで開始
const loadUserState = () => {
  try { return JSON.parse(localStorage.getItem(GROUP_COLLAPSED_LS) || '{}') || {}; }
  catch { return {}; }
};
const saveUserState = (obj) => {
  try { localStorage.setItem(GROUP_COLLAPSED_LS, JSON.stringify(obj)); }
  catch { /* ignore */ }
};
// 表示すべきか判定 — ユーザーが明示的にセットしていればそれを尊重、無ければデフォルト
const isOpenFor = (userState, key) => (userState[key] != null ? !!userState[key] : DEFAULT_OPEN.has(key));

const GroupSection = ({ groupKey, meta, rows, testidType, onLink, onRegister, forceOpen, subtle, open, onToggle }) => {
  const isOpen = forceOpen ? true : open;
  const Icon = meta.Icon;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`omada-group-${groupKey}`}>
      <button type="button" onClick={() => onToggle(groupKey)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 transition-colors"
        data-testid={`omada-group-toggle-${groupKey}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md border ${meta.cls}`}><Icon size={12} weight="bold" /></span>
        <span className="text-xs font-semibold text-zinc-700 flex-1 text-left">{meta.label}</span>
        <span className="text-[10px] font-semibold text-zinc-500 tabular-nums">{rows.length}</span>
        <CaretDown size={12} weight="bold" className={`text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <ul className="divide-y divide-zinc-200 border-t border-zinc-200">
          {rows.map((row) => (
            <li key={row.mac} className={`flex items-center gap-2 px-3 py-2 group hover:bg-sky-50/40 transition-colors ${subtle ? 'text-zinc-500' : ''}`}
              data-testid={`it-unmatched-${testidType === 'device' ? 'device' : 'client'}-${row.mac}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[11px]">
                  {row.type && DEVICE_TYPE_LABEL[row.type] && (
                    <span className="px-1 py-0 rounded bg-sky-100 text-sky-700 text-[9px] font-bold shrink-0">{DEVICE_TYPE_LABEL[row.type]}</span>
                  )}
                  <span className={`font-semibold truncate ${subtle ? 'text-zinc-500 font-mono' : 'text-zinc-800'}`}>
                    {row.name || row.model || row.mac}
                  </span>
                  {row.connection && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] text-zinc-400 shrink-0">
                      {row.connection === '無線' && <WifiHigh size={9} weight="bold" />} {row.connection}
                    </span>
                  )}
                </div>
                <p className="text-[9px] font-mono text-zinc-400 mt-0.5 truncate">
                  {row.mac}{row.ip ? ` ・ ${row.ip}` : ''}
                </p>
              </div>
              <RowActions row={row} testidType={testidType} onLink={onLink} onRegister={onRegister} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Omada 未登録機器（インフラ + クライアント）を種別ごとに折り畳み表示。
export default function UnmatchedOmadaList({ unmatched, onRegister, onLink }) {
  const [query, setQuery] = useState('');
  const [userState, setUserState] = useState(loadUserState);
  // 検索時は全グループを強制展開して見逃しを防ぐ
  const forceOpenAll = query.trim().length > 0;
  const toggleGroup = (key) => setUserState((prev) => {
    const currentlyOpen = isOpenFor(prev, key);
    const next = { ...prev, [key]: !currentlyOpen };
    saveUserState(next);
    return next;
  });

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (row) => !q || [row.name, row.model, row.mac, row.ip]
      .some((v) => String(v || '').toLowerCase().includes(q));

    const devices = (unmatched?.devices || []).filter(match);
    const clientsAll = (unmatched?.clients || []).filter(match);

    // クライアントを分類
    const buckets = { mobile: [], laptop: [], printer: [], media: [], named: [], unknown: [] };
    clientsAll.forEach((c) => {
      const name = c.name;
      const kind = !name || isMacLike(name) ? 'unknown' : inferKind(name);
      buckets[kind].push(c);
    });
    return { devices, buckets };
  }, [unmatched, query]);

  const total = (unmatched?.devices?.length || 0) + (unmatched?.clients?.length || 0);
  const filteredTotal = groups.devices.length + Object.values(groups.buckets).reduce((s, b) => s + b.length, 0);

  return (
    <div className="space-y-2" data-testid="it-omada-unmatched">
      <div className="flex items-baseline gap-2">
        <p className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
          <SealQuestion size={13} weight="duotone" className="text-sky-600" />
          未登録機器
          <span className="text-[10px] font-normal text-zinc-400">Omada検出</span>
        </p>
        <span className="ml-auto text-[10px] font-semibold text-sky-600 bg-sky-50 border border-sky-100 rounded-full px-1.5 py-0.5 tabular-nums">{total}</span>
      </div>

      <div className="relative">
        <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="名前・MAC・IP で絞込"
          className="pl-7 h-8 text-[11px] bg-white"
          data-testid="omada-unmatched-search" />
      </div>

      <p className="text-[10px] text-zinc-400 leading-tight">
        <b className="text-sky-700">+登録</b> で新規資産化・
        <b className="text-emerald-700">紐付け</b> で既存資産にMACを結び付けます。
      </p>

      {query && filteredTotal === 0 && (
        <p className="text-[11px] text-zinc-400 text-center py-4">該当する機器がありません</p>
      )}

      {groups.devices.length > 0 && (
        <GroupSection groupKey="infra"
          meta={{ label: 'インフラ機器', Icon: WifiHigh, cls: 'text-sky-700 bg-sky-100 border-sky-200' }}
          rows={groups.devices} testidType="device"
          onLink={onLink} onRegister={onRegister}
          forceOpen={forceOpenAll} open={isOpenFor(userState, 'infra')} onToggle={toggleGroup} />
      )}
      {['mobile', 'laptop', 'printer', 'media', 'named'].map((k) => groups.buckets[k].length > 0 && (
        <GroupSection key={k} groupKey={k} meta={KIND_META[k]}
          rows={groups.buckets[k]} testidType="client"
          onLink={onLink} onRegister={onRegister}
          forceOpen={forceOpenAll} open={isOpenFor(userState, k)} onToggle={toggleGroup} />
      ))}
      {groups.buckets.unknown.length > 0 && (
        <GroupSection groupKey="unknown" meta={KIND_META.unknown}
          rows={groups.buckets.unknown} testidType="client"
          onLink={onLink} onRegister={onRegister}
          forceOpen={forceOpenAll} open={isOpenFor(userState, 'unknown')} onToggle={toggleGroup} subtle />
      )}
    </div>
  );
}
